import { useCallback, useEffect, useState } from 'react';
import type { RefObject } from 'react';

import { findChapterWithIndexAtTime } from './chapterTimeFormat';
import { parseChaptersWebVtt, type ParsedChapter } from './parseChaptersWebVtt';

export type { ParsedChapter } from './parseChaptersWebVtt';

function activeChapterFromList(
  chapters: ParsedChapter[],
  time: number,
): { title: string | null; index: number | null } {
  const hit = findChapterWithIndexAtTime(chapters, time);
  if (!hit) return { title: null, index: null };
  const t = hit.chapter.title.trim();
  return { title: t || null, index: hit.index };
}

type UseCustomPlayerChaptersArgs = {
  videoRef: RefObject<HTMLVideoElement | null>;
  chaptersUrl: string | null;
  enabled: boolean;
};

/**
 * Loads chapter WebVTT via `fetch` + parse. Native `<track kind="chapters">` is unreliable across
 * browsers (cues often stay empty while `mode` is `disabled` or load never completes next to MSE).
 */
export function useCustomPlayerChapters({ videoRef, chaptersUrl, enabled }: UseCustomPlayerChaptersArgs) {
  const [chapters, setChapters] = useState<ParsedChapter[]>([]);
  const [activeTitle, setActiveTitle] = useState<string | null>(null);
  const [activeChapterIndex, setActiveChapterIndex] = useState<number | null>(null);

  const seekTo = useCallback((seconds: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, seconds);
  }, [videoRef]);

  useEffect(() => {
    if (!enabled || !chaptersUrl) {
      setChapters([]);
      setActiveTitle(null);
      setActiveChapterIndex(null);
      return undefined;
    }

    const ac = new AbortController();
    void (async () => {
      try {
        const res = await fetch(chaptersUrl, {
          signal: ac.signal,
          credentials: 'omit',
          mode: 'cors',
        });
        if (!res.ok || ac.signal.aborted) return;
        const text = await res.text();
        if (ac.signal.aborted) return;
        setChapters(parseChaptersWebVtt(text));
      } catch {
        if (!ac.signal.aborted) {
          setChapters([]);
        }
      }
    })();

    return () => {
      ac.abort();
    };
  }, [chaptersUrl, enabled]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || chapters.length === 0) {
      setActiveTitle(null);
      setActiveChapterIndex(null);
      return undefined;
    }

    const refresh = () => {
      const { title, index } = activeChapterFromList(chapters, video.currentTime);
      setActiveTitle(title);
      setActiveChapterIndex(index);
    };

    video.addEventListener('timeupdate', refresh);
    video.addEventListener('seeked', refresh);
    refresh();

    return () => {
      video.removeEventListener('timeupdate', refresh);
      video.removeEventListener('seeked', refresh);
    };
  }, [chapters, videoRef]);

  return { chapters, activeTitle, activeChapterIndex, seekTo };
}
